import { type FeatureFlagsState, FeatFlag } from './flagTypes';

const defaultState: Readonly<FeatureFlagsState> = {
  depthPeelingLayers: 0,
  disableTransparencyAngleBasedCompensation: false,
  disableTransparencyFlatCompensation: false,
  parallax: false,
  premultipliedAlphaLogos: true,
  wireframeFabrics: false,
} as const;

const globalState: FeatureFlagsState = {
  depthPeelingLayers: defaultState.depthPeelingLayers,
  disableTransparencyAngleBasedCompensation: defaultState.disableTransparencyAngleBasedCompensation,
  disableTransparencyFlatCompensation: defaultState.disableTransparencyFlatCompensation,
  parallax: defaultState.parallax,
  premultipliedAlphaLogos: defaultState.premultipliedAlphaLogos,
  wireframeFabrics: defaultState.wireframeFabrics,
};

/**
 * Initialize flags state.
 * @param state - New state.
 */
export const init = (state?: Partial<FeatureFlagsState>) => {
  // Complete state with default values
  state ??= {};
  state.depthPeelingLayers ??= defaultState.depthPeelingLayers;
  state.disableTransparencyAngleBasedCompensation ??= defaultState.disableTransparencyAngleBasedCompensation;
  state.disableTransparencyFlatCompensation ??= defaultState.disableTransparencyFlatCompensation;
  state.parallax ??= defaultState.parallax;
  state.premultipliedAlphaLogos ??= defaultState.premultipliedAlphaLogos;
  state.wireframeFabrics ??= defaultState.wireframeFabrics;
  // Assign initialization values
  globalState.depthPeelingLayers = state.depthPeelingLayers;
  globalState.disableTransparencyAngleBasedCompensation = state.disableTransparencyAngleBasedCompensation;
  globalState.disableTransparencyFlatCompensation = state.disableTransparencyFlatCompensation;
  globalState.parallax = state.parallax;
  globalState.premultipliedAlphaLogos = state.premultipliedAlphaLogos;
  globalState.wireframeFabrics = state.wireframeFabrics;
};

/**
 * Query for flags status.
 * @param key - Flags key.
 */
export const isEnabled = (key: FeatFlag): boolean => {
  switch (key) {
    case FeatFlag.DEPTH_PEELING:
      return globalState.depthPeelingLayers > 0;
    case FeatFlag.DISABLE_TRANSPARENCY_ANGLE_BASED_COMPENSATION:
      return globalState.disableTransparencyAngleBasedCompensation;
    case FeatFlag.DISABLE_TRANSPARENCY_FLAT_COMPENSATION:
      return globalState.disableTransparencyFlatCompensation;
    case FeatFlag.PARALLAX:
      return globalState.parallax;
    case FeatFlag.PREMULTIPLIED_ALPHA_LOGOS:
      return Boolean(globalState.premultipliedAlphaLogos);
    case FeatFlag.WIREFRAME_FABRICS:
      return globalState.wireframeFabrics;
    default:
      return false;
  }
};

/**
 * Get flags state.
 */
export const getState = (): Readonly<FeatureFlagsState> => {
  return globalState;
};

/**
 * Get flag value.
 * @param flag - Feature.
 */
export const getValue = (flag: FeatFlag): any => {
  switch (flag) {
    case FeatFlag.DEPTH_PEELING:
      return globalState.depthPeelingLayers;
    default:
      return false;
  }
};
